import { useEffect, useRef, useState, type CSSProperties } from 'react';
import drakochiLava from '../../levels/drakochi/obstacles/lava.png';
import {
  DRAKOCHI_OVERLAY_FADE_MS,
  DRAKOCHI_OVERLAY_HOLD_MS,
} from './gameplayConstants';
import './LevelDrakochiOverlay.css';

type DrakochiPhase = 'in' | 'hold' | 'out';

const PHASE_MS: Record<DrakochiPhase, number> = {
  in: DRAKOCHI_OVERLAY_FADE_MS,
  hold: DRAKOCHI_OVERLAY_HOLD_MS,
  out: DRAKOCHI_OVERLAY_FADE_MS,
};

const NEXT_PHASE: Record<DrakochiPhase, DrakochiPhase | null> = {
  in: 'hold',
  hold: 'out',
  out: null,
};

const LAVA_SIDES = [
  { id: 'left', side: 'left' },
  { id: 'right', side: 'right' },
] as const;

type LevelDrakochiOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

export function LevelDrakochiOverlay({
  isPaused,
  onComplete,
}: LevelDrakochiOverlayProps) {
  const [phase, setPhase] = useState<DrakochiPhase>('in');
  const [shown, setShown] = useState(false);
  const remainingMsRef = useRef(PHASE_MS.in);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setShown(true);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    if (phase === 'out') {
      setShown(false);
    }
  }, [phase]);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();
    const tick = (now: number) => {
      remainingMsRef.current -= now - lastNow;
      lastNow = now;
      if (remainingMsRef.current > 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      const next = NEXT_PHASE[phase];
      if (next == null) {
        onCompleteRef.current();
        return;
      }
      remainingMsRef.current = PHASE_MS[next];
      setPhase(next);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [phase, isPaused]);

  const className = [
    'level-drakochi-overlay',
    shown ? 'level-drakochi-overlay--shown' : '',
    isPaused ? 'level-drakochi-overlay--paused' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      style={
        {
          transitionDuration: `${DRAKOCHI_OVERLAY_FADE_MS}ms`,
        } as CSSProperties
      }
      aria-hidden
    >
      {LAVA_SIDES.map((lava) => (
        <div
          key={lava.id}
          className={`level-drakochi-overlay__lava level-drakochi-overlay__lava--${lava.side}`}
        >
          <img
            className="level-drakochi-overlay__art"
            src={drakochiLava}
            alt=""
          />
        </div>
      ))}
    </div>
  );
}
